// src/pages/RecycleBinPage.js
// Admin-only page. Shows soft-deleted items for 30 days before they are
// purged — one tab per type, each hitting that type's own recycle-bin
// endpoint/table so nothing gets mixed together.
import React, { useEffect, useState, useCallback } from "react";
import { formatDate } from "../utils/dates";

const TABS = [
  { key: "saas",    label: "Deleted SaaS Applications",  endpoint: "saas-applications-recycle-bin",   noun: "SaaS application"  },
  { key: "local",   label: "Deleted Local Applications", endpoint: "local-applications-recycle-bin",  noun: "local application" },
  { key: "shared",  label: "Deleted Shared Credentials", endpoint: "shared-credentials-recycle-bin",  noun: "shared credential" },
  { key: "deletedUsers", label: "Unrecognized Users",     endpoint: null,                               noun: "user"              },
];

export default function RecycleBinPage() {
  const [tab, setTab] = useState("saas");
  const activeTab = TABS.find(t => t.key === tab);

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Recycle Bin</div>
          <div className="page-subtitle">Deleted items and users with no Entra account</div>
        </div>
      </div>

      <div style={{ display:"flex", marginBottom:20, flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:"8px 18px", border:"none", borderRadius:0, background:"transparent",
            cursor:"pointer", fontWeight:500, fontSize:13, whiteSpace:"nowrap",
            color:        tab===t.key ? "var(--purple)" : "var(--gray-500)",
            borderBottom: tab===t.key ? "2px solid var(--purple)" : "2px solid transparent",
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "deletedUsers" && <DeletedUsersSection />}

      {activeTab?.endpoint && (
        <ResourceRecycleBinSection key={activeTab.key} endpoint={activeTab.endpoint} noun={activeTab.noun} />
      )}
    </div>
  );
}

// ── Generic recycle bin section — one per type, each pointed at its own endpoint ──
function ResourceRecycleBinSection({ endpoint, noun }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`/api/${endpoint}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (item) => {
    if (!window.confirm(`Restore "${item.name}" and all its users?`)) return;
    setWorking(item.id);
    try {
      const res = await fetch(`/api/${endpoint}/${item.id}`, { method: "POST" });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      await load();
    } catch (err) { alert("Restore failed: " + err.message); }
    finally { setWorking(null); }
  };

  const handlePurge = async (item) => {
    if (!window.confirm(`Permanently delete "${item.name}"? This cannot be undone.`)) return;
    setWorking(item.id);
    try {
      await fetch(`/api/${endpoint}/${item.id}`, { method: "DELETE" });
      await load();
    } catch (err) { alert("Purge failed: " + err.message); }
    finally { setWorking(null); }
  };

  return (
    <>
      <div style={{ background: "var(--purple-light)", borderRadius: "var(--radius)", padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "var(--purple-dark)" }}>
        <strong>ℹ️ How it works:</strong> Deleting a {noun} moves it here along with all its users and audit history. You can restore it at any time within 30 days. After 30 days it is permanently and irreversibly purged.
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="card empty-state">
          <h3>Recycle bin is empty</h3>
          <p>Deleted {noun}s will appear here for 30 days before being permanently removed.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50)" }}>
                {["Name", "Category", "Deleted", "Users", ""].map(h => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const isWorking = working === item.id;
                const deletedAt = new Date(item.deletedAt);
                const purgeAt   = new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
                const daysLeft  = Math.max(0, Math.ceil((purgeAt - Date.now()) / (1000 * 60 * 60 * 24)));
                return (
                  <tr key={item.id}>
                    <td style={{ padding: "11px 16px" }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{item.name}</div>
                      {item.description && <div style={{ fontSize: 12, color: "var(--gray-500)" }}>{item.description}</div>}
                    </td>
                    <td style={{ padding: "11px 16px" }}>
                      {item.category ? <span className="badge badge-info" style={{ fontSize: 11 }}>{item.category}</span> : "—"}
                    </td>
                    <td style={{ padding: "11px 16px", fontSize: 13, color: "var(--gray-500)" }}>
                      <div>{formatDate(item.deletedAt)}</div>
                      <div style={{ fontSize: 11, color: daysLeft <= 5 ? "var(--red)" : "var(--gray-400)" }}>
                        {daysLeft === 0 ? "Purges today" : `${daysLeft}d remaining`}
                      </div>
                    </td>
                    <td style={{ padding: "11px 16px", fontSize: 13, color: "var(--gray-500)" }}>
                      {item.userCount ?? item.users?.length ?? 0} user{(item.userCount ?? item.users?.length ?? 0) !== 1 ? "s" : ""}
                    </td>
                    <td style={{ padding: "11px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 12, marginRight: 6 }}
                        onClick={() => handleRestore(item)}
                        disabled={isWorking}
                        title={`Restore this ${noun} and all its users`}>
                        {isWorking ? <span className="spinner" style={{ width: 12, height: 12 }} /> : "↩ Restore"}
                      </button>
                      <button
                        className="btn-danger"
                        style={{ fontSize: 12 }}
                        onClick={() => handlePurge(item)}
                        disabled={isWorking}
                        title="Permanently delete — cannot be undone">
                        🗑 Purge
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Deleted Users section ─────────────────────────────────────────────────────
export function DeletedUsersSection() {
  const [users,     setUsers]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [working,   setWorking]   = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmTxt,setConfirmTxt]= useState("");
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/deleted-users").then(r=>r.json()).then(setUsers).catch(()=>setUsers([])).finally(()=>setLoading(false));
  }, []);
  useEffect(()=>{ load(); }, [load]);

  const doAction = async (user, action) => {
    if (action==="delete" && confirmTxt !== "CONFIRM") { setError("Type CONFIRM to confirm deletion."); return; }
    setWorking(user.upn); setError(""); setSuccess("");
    try {
      const res = await fetch("/api/deleted-users", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ action, upn:user.upn, displayName:user.displayName, email:user.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUsers(prev=>(prev||[]).filter(u=>u.upn!==user.upn));
      setSuccess(action==="convert"
        ? `${user.displayName} converted to Custom User — assignments preserved.`
        : `${user.displayName} removed from portal (${data.systemsUpdated} system(s) updated).`);
      setConfirmId(null); setConfirmTxt("");
    } catch(err) { setError(err.message); }
    finally { setWorking(null); }
  };

  if (loading) return <div style={{textAlign:"center",padding:24}}><span className="spinner"/></div>;

  return (
    <div>
      {error   && <div style={{background:"var(--red-light)",color:"var(--red)",borderRadius:"var(--radius)",padding:"8px 12px",fontSize:13,marginBottom:10}}>{error}</div>}
      {success && <div style={{background:"#D1FAE5",color:"#065F46",borderRadius:"var(--radius)",padding:"8px 12px",fontSize:13,marginBottom:10}}>✓ {success}</div>}

      <p style={{fontSize:13,color:"var(--gray-600)",marginBottom:14}}>
        These users have active system assignments but no matching Entra ID or Custom User account — either because their Entra account was removed, or because something (like an API sync) added them by email before they existed anywhere else in the portal. Convert them to Custom Users to keep their assignments, or remove them from the portal entirely.
      </p>

      {!users?.length ? (
        <div style={{fontSize:13,color:"var(--gray-400)",fontStyle:"italic"}}>
          No unrecognized users found — everyone with portal assignments currently exists in Entra ID or as a Custom User.
        </div>
      ) : (
        <div className="card" style={{padding:0,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"var(--gray-50)",}}>
                {["Name","UPN","Assigned Systems","Actions"].map(h=>(
                  <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:"var(--gray-500)"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u=>(
                <React.Fragment key={u.upn}>
                  <tr>
                    <td style={{padding:"10px 14px",fontWeight:500,fontSize:13}}>{u.displayName}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:"var(--gray-500)"}}>{u.upn}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:"var(--gray-500)"}}>
                      {(u.systems||[]).map(s=>s.systemName).join(", ") || "—"}
                    </td>
                    <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}>
                      <button className="btn-secondary" style={{fontSize:11,marginRight:6}}
                        disabled={!!working} onClick={()=>doAction(u,"convert")}>
                        Convert to Custom User
                      </button>
                      <button className="btn-danger" style={{fontSize:11}}
                        disabled={!!working} onClick={()=>{setConfirmId(u.upn);setConfirmTxt("");setError("");}}>
                        Remove from Portal
                      </button>
                    </td>
                  </tr>
                  {confirmId===u.upn && (
                    <tr>
                      <td colSpan={4} style={{padding:"10px 14px",background:"#FFF1F2"}}>
                        <div style={{fontSize:13,color:"#7A0000",marginBottom:8}}>
                          ⚠️ This will remove <strong>{u.displayName}</strong> from <strong>{u.systems?.length} system(s)</strong>. This cannot be undone.
                          Type <code style={{background:"rgba(0,0,0,0.08)",padding:"1px 6px",borderRadius:4}}>CONFIRM</code> to proceed.
                        </div>
                        <div style={{display:"flex",gap:8,alignItems:"center"}}>
                          <input value={confirmTxt} onChange={e=>setConfirmTxt(e.target.value)}
                            placeholder="CONFIRM" style={{fontSize:13,fontWeight:600,letterSpacing:2,width:140}} />
                          <button className="btn-danger" style={{fontSize:12}}
                            disabled={working===u.upn||confirmTxt!=="CONFIRM"} onClick={()=>doAction(u,"delete")}>
                            {working===u.upn ? <><span className="spinner" style={{width:12,height:12}}/> Removing…</> : "Confirm Remove"}
                          </button>
                          <button className="btn-ghost" style={{fontSize:12}} onClick={()=>{setConfirmId(null);setConfirmTxt("");setError("");}}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
